import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload } from '@domain-group/react-hooks'

import { mergeAndStringifyEntityObjects } from '@functions/entity'
import { baseURL } from '@functions/api'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as apiLogActions from '@redux/modules/apiLog'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/apiLog'
import type { ApiLogModel } from '@models/types'

export const generateUrls = (apiLog: Partial<ApiLogModel> = {}) => ({
  apiLogsIndexUrl: '#/systemManager/apiLogs',
  exportApiLog: `${baseURL}/api_logs/${apiLog.id}/export.json`,
})

type UpdateApiLogPayload = {
  apiLog: ApiLogModel,
  apiLogParams: Partial<ApiLogModel>,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const updateApiLog = (params: UpdateApiLogPayload) => {
  const {
    apiLog, apiLogParams, dispatch, requestOptions,
  } = params
  const { updateApiLog: updateFn } = apiLogActions

  const updatedParams = mergeAndStringifyEntityObjects(apiLog, apiLogParams, [])

  return dispatch(updateFn(updatedParams, requestOptions))
}

type EditApiLogPayload = {
  apiLog: ApiLogModel,
  showEditApiLogModal: Function,
}

const editApiLog = (params: EditApiLogPayload) => new Promise((resolve, reject) => {
  const { apiLog, showEditApiLogModal } = params

  if (showEditApiLogModal){
    const payload = {
      apiLog,
    }

    showEditApiLogModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showEditApiLogModal not defined in PageContext callbacks'))
})

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useApiLogForm(
  apiLog: Partial<ApiLogModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
): UseFormPayload {
  const { customRequiredFields = [], validateOn } = options

  const apiLogForm = useForm(
    defaultFormState,
    { entity: apiLog, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [apiLog.id, apiLog.cache_key],
  )

  return apiLogForm
}

export function useRelations(apiLog: Partial<ApiLogModel> = {}) {
  const { actioned_by_id } = apiLog

  const users = useSelector(reduxState => reduxState.entities.users)

  const user = users[actioned_by_id!] || {}

  return { user }
}

function useApiLog(initEntity: Partial<ApiLogModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: apiLog }: { entity: ApiLogModel} = useLatestEntity(initEntity, 'apiLogs')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)
  const { showEditApiLogModal } = callbacks || {}

  const { user } = useRelations(apiLog)

  const { loading, updating } = useSelector(reduxState => reduxState.apiLogs)

  return {
    apiLog,
    callbacks: {
      editApiLog: () => editApiLog({ apiLog, showEditApiLogModal }),
      updateApiLog: (apiLogParams: Partial<ApiLogModel>, requestOptions?: EntityOptions) => updateApiLog({
        apiLog, apiLogParams, dispatch, requestOptions,
      }),
    },
    loading,
    updating,
    urls: generateUrls(apiLog),
    user,
  }
}

export default useApiLog
